/**
 * Sandboxed iframe preview of supported step screens, reconstructed from step
 * options via renderToStaticMarkup. No live components, no network, no store.
 * Inline class strings duplicated from step-shell.ts to avoid
 * importing runtime code into this static-only module.
 */
import { DoorOfferPreview } from "./beta-door-preview";
import { renderToStaticMarkup } from "react-dom/server";
import type { CSSProperties, ReactElement } from "react";
import type { DetailResponse } from "./beta-step-detail";

import keyPoster from "../stepper/steptypes/key-spin-poster.jpg";
import DeliveredScreen from "../stepper/steptypes/WebSmDelivered_type";
import RejectScreen from "../stepper/steptypes/WebSmReject_type";

type Opts = Record<string, string>;
const o = (opts: Opts, k: string, d = "") => opts[k] || d;

function vars(accent: string, bg: string, btnText = "#e6d3a4"): CSSProperties {
  return {
    "--sm-bg": bg,
    "--sm-accent": accent,
    "--sm-accent-glow": accent + "22",
    "--sm-accent-soft": accent + "59",
    "--sm-accent-edge": accent + "6b",
    "--sm-accent-strong": accent + "80",
    "--sm-btn-text": btnText,
  } as CSSProperties;
}

// Classes from step-shell.ts (static copies, not imports)
const SEC =
  "relative flex h-[100dvh] items-center justify-center overflow-hidden font-sans bg-[var(--sm-bg)]";
const ACT =
  "cursor-pointer border border-[var(--sm-accent-strong)] bg-[linear-gradient(180deg,#7d1d18,#511110)] text-[var(--sm-btn-text)] shadow-[0_6px_18px_rgba(0,0,0,.45),inset_0_1px_0_rgba(255,255,255,.08)]";
const CHOICE = `${ACT} flex-1 min-w-[148px] rounded-lg px-4 py-3 text-[14.5px] font-semibold tracking-[0.2px]`;

const LINE_CLS: Record<string, string> = {
  strong:
    "text-center mb-4 text-[21px] leading-[1.32] font-bold text-[#f4eddb]",
  em: "text-center mt-4 text-[16px] leading-[1.5] text-[#a99d86] italic",
  bullet:
    "text-left mx-auto mt-2.5 max-w-[340px] text-[15.5px] leading-[1.5] text-[#e2d6bd]",
  normal: "text-center my-[7px] text-[17px] leading-[1.55] text-[#ddd3bf]",
};

// --- Renderers for each supported step type (post-animation state) ---

function Delivered(opts: Opts) {
  return (
    <DeliveredScreen getOpt={(key) => opts[key] || ""} dispatch={() => {}} />
  );
}
function Reject(opts: Opts) {
  return <RejectScreen getOpt={(key) => opts[key] || ""} dispatch={() => {}} />;
}

function Order(opts: Opts) {
  const bg = o(opts, "BgColor", "#0a0809");
  const accent = o(opts, "AccentColor", "#d6a94e");
  const btnText = o(opts, "ButtonTextColor", "#e6d3a4");
  const raw = o(
    opts,
    "Lines",
    "There are more of us than you'd guess.\nBut nobody just joins. You earn your way in if you're good enough.\n_ Your first secret mission begins now:",
  );
  const lines = raw
    .split("\n")
    .map((l) => l.replace(/\s+$/, ""))
    .filter((l) => l.trim())
    .map((l) => {
      if (l.startsWith("* "))
        return { kind: "strong" as const, text: l.slice(2) };
      if (l.startsWith("_ ")) return { kind: "em" as const, text: l.slice(2) };
      if (l.startsWith("- ") || l.startsWith("> "))
        return { kind: "bullet" as const, text: l.slice(2) };
      return { kind: "normal" as const, text: l };
    });
  // Strip **word** to plain uppercase, no animation in static preview
  const txt = (s: string) => {
    const m = s.match(/\*\*(.+?)\*\*/);
    return m
      ? s.slice(0, m.index!) +
          m[1].toUpperCase() +
          s.slice(m.index! + m[0].length)
      : s;
  };
  const boxBody = opts.BoxBody === "1";
  const label1 = o(opts, "LabelForCLICK1", "I'm in.");
  const label2 = opts.LabelForCLICK2 || "";
  const firstTwo = lines.slice(0, 2);
  const lead = opts.Lead;
  const rest = lead ? [{ kind: "em" as const, text: lead }] : lines.slice(2);

  return (
    <section
      style={vars(accent, bg, btnText)}
      className={`${SEC} px-4 py-[clamp(8px,3dvh,56px)]`}
    >
      <div className="relative z-[2] mx-auto w-full max-w-[432px] px-[26px] pt-[clamp(4px,2dvh,30px)] pb-[clamp(4px,2dvh,26px)] text-center">
        {firstTwo.map((ln, i) => {
          const line = (
            <div className={LINE_CLS[ln.kind]}>
              {ln.kind === "bullet" ? (
                <>
                  {"\u25b8\u00a0"}
                  {txt(ln.text)}
                </>
              ) : (
                txt(ln.text)
              )}
            </div>
          );
          return boxBody && ln.kind !== "strong" ? (
            <div
              key={i}
              className="mx-auto mt-1 rounded-md border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18/0.9)] px-5 py-4 shadow-[0_16px_44px_rgba(0,0,0,.5)]"
            >
              {line}
            </div>
          ) : (
            <div key={i}>{line}</div>
          );
        })}
        {rest.length > 0 && (
          <div className="mt-[clamp(10px,3dvh,24px)]">
            {rest.map((ln, i) => (
              <div
                key={i}
                className={
                  ln.kind === "em"
                    ? "text-center text-[clamp(19px,5.2cqw,23px)] leading-[1.3] font-medium tracking-[-.01em] text-balance text-[#f0e6d2]"
                    : LINE_CLS[ln.kind]
                }
              >
                {ln.kind === "bullet" ? (
                  <>
                    {"\u25b8\u00a0"}
                    {txt(ln.text)}
                  </>
                ) : (
                  txt(ln.text)
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-[clamp(12px,3dvh,26px)] flex flex-col items-stretch gap-[11px] sm:flex-row sm:flex-wrap sm:justify-center">
          <button type="button" className={CHOICE}>
            {label1}
          </button>
          {label2 ? (
            <button type="button" className={CHOICE}>
              {label2}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Start(opts: Opts) {
  const accent = o(opts, "AccentColor", "#d6a94e");
  const titleColor = o(opts, "TitleColor", "#f4eddb");
  const messageColor = o(opts, "MessageColor", "#e7ddca");
  const btnText = o(opts, "ButtonTextColor", "#e6d3a4");
  const brand = opts.Brand || "";
  const title = o(opts, "Title", "Ashley gave you a one-use key.");
  const subtitle = o(
    opts,
    "Subtitle",
    "Let's see if they were right about you.",
  );
  const message = o(
    opts,
    "Message",
    "Have you ever had a feeling, deep in your soul, that you were meant to be doing something far more important - something that matters a lot more than what you have been doing?",
  );
  const label1 = o(opts, "LabelForCLICK1", "Yes, I get that feeling a lot");
  const label2 = opts.LabelForCLICK2 || "";
  const sv = {
    ...vars(accent, "#000", btnText),
    "--sm-brand-glow": accent + "59",
    "--sm-title": titleColor,
    "--sm-message": messageColor,
  } as CSSProperties;

  return (
    <section style={sv} className={`${SEC} bg-none px-4 py-[70px]`}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_54%,rgba(0,0,0,.58))]" />
      {brand ? (
        <span className="absolute top-[15px] left-[22px] z-[3] font-stencil text-[13px] tracking-[3px] text-[var(--sm-accent)] [text-shadow:0_0_12px_var(--sm-brand-glow)]">
          {brand}
        </span>
      ) : null}
      {/* Settled screen state, with the key poster instead of autoplay. */}
      <div className="relative z-[3] mx-auto w-full max-w-[432px] px-[26px] pt-[20dvh] pb-[26px] text-center">
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "10dvh",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(14deg)",
            mixBlendMode: "screen",
          }}
        >
          <img
            src={keyPoster}
            alt=""
            style={{
              height: "19.5dvh",
              width: "auto",
              clipPath: "inset(13.5% 27% 13.5% 27%)",
            }}
          />
        </div>
        <div className="-mx-[26px] text-[clamp(15px,5.4cqw,24px)] leading-[1.25] font-bold tracking-[-0.2px] whitespace-nowrap text-[var(--sm-title)]">
          {title}
        </div>
        {subtitle ? (
          <div className="mt-[14px] mb-[22px] text-[clamp(12.5px,4.3cqw,19px)] leading-[1.35] text-[#a99d86] italic">
            {subtitle}
          </div>
        ) : null}
        {message ? (
          <div className="rounded-xl border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18/0.92)] px-[22px] py-5 text-[clamp(18px,4.8cqw,23px)] leading-[1.5] font-normal text-pretty text-[var(--sm-message)] shadow-[0_20px_60px_rgba(0,0,0,.55)]">
            {message}
          </div>
        ) : null}
        <div className="mt-6 flex flex-col items-stretch gap-[11px] sm:flex-row sm:flex-wrap sm:justify-center">
          <button type="button" className={CHOICE}>
            {label1}
          </button>
          {label2 ? (
            <button type="button" className={CHOICE}>
              {label2}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// --- Supported type map ---

const RENDERERS: Record<string, (opts: Opts) => ReactElement> = {
  WebSmDelivered: Delivered,
  WebSmReject: Reject,
  WebSmOrder: Order,
  WebSmStart: Start,
};

// --- Public component ---

export function BetaScreenPreview({ preview }: { preview: DetailResponse }) {
  const renderer = RENDERERS[preview.step.type];
  const isDoors = preview.step.type === "WebSmDoors";
  if (isDoors && !preview.doorOffer)
    return (
      <div className="beta-phone-empty">
        The offered door pair was not recorded for this visit. Any saved choice
        is shown in the journey.
      </div>
    );
  if (!renderer && !isDoors) {
    return (
      <div className="beta-phone-empty">
        Preview unavailable for {preview.step.type}.
      </div>
    );
  }

  const markup = renderToStaticMarkup(
    isDoors && preview.doorOffer ? (
      <DoorOfferPreview offer={preview.doorOffer} options={preview.options} />
    ) : (
      renderer(preview.options)
    ),
  );
  const origin = window.location.origin;
  const srcDoc = [
    "<!doctype html><html><head>",
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; connect-src 'none'; form-action 'none'; style-src ${origin} 'unsafe-inline'; img-src ${origin} https:; font-src ${origin}">`,
    '<link rel="stylesheet" href="/static/secretmissions/assets/index.css">',
    "<style>html,body{margin:0;height:100%;overflow:auto;background:#000;container-type:inline-size}</style>",
    "</head><body>",
    markup,
    "</body></html>",
  ].join("");

  return (
    <iframe
      sandbox=""
      srcDoc={srcDoc}
      title="Screen preview"
      className="beta-phone-iframe"
    />
  );
}
